import React, { useMemo } from 'react';
import BookingGalleryCollage from './BookingGalleryCollage';
import BookingInfoBar from './BookingInfoBar';
import useStylesheet from '../hooks/useStylesheet';
import { getOpenDaysLabel, getSportCourtCount, getMapLinkUrl } from '../utils/bookingUtils';
import { GEIST_FONT_STYLESHEET_URL } from '../constants/bookingConst';

/**
 * The top of the guest booking screen in UI design Version 2 (Config ->
 * Theme -> UI Design): the package button, the framed photo collage with the
 * club's info panel, and the info bar. Takes the place of App.jsx's Version 1
 * intro (plain collage + "Book a Court at …" heading).
 *
 * @param {object} props
 * @param {object} props.config - club config (text and facts)
 * @param {string|null} props.sportId - the sport on screen, for the court count
 * @param {string} [props.sportName] - shown in the eyebrow ("Book a Pickleball court at")
 * @param {string} props.clubName
 * @param {{id?: string, url: string}[]} props.photos - collage photos, main first (at most 4)
 * @param {number} props.totalPhotos - every gallery photo, for "Show all photos"
 * @param {(index: number) => void} props.onOpenPhoto - opens the lightbox at that photo
 * @param {React.ReactNode} [props.packageCta] - the package entry button, when the club has one
 * @returns {JSX.Element}
 */
function BookingIntroV2({ config, sportId, sportName, clubName, photos, totalPhotos, onOpenPhoto, packageCta }) {
  useStylesheet(GEIST_FONT_STYLESHEET_URL);

  // Short facts for the info panel, each with its icon, skipping any the
  // club hasn't configured so the panel never shows an empty line.
  const features = useMemo(() => {
    const courtCount = getSportCourtCount(config, sportId);
    return [
      { key: 'days', icon: 'days', label: getOpenDaysLabel(config) },
      { key: 'courts', icon: 'courts', label: courtCount > 0 ? `${courtCount} court${courtCount === 1 ? '' : 's'}` : '' },
      { key: 'location', icon: 'location', label: (config?.locationText || '').trim() },
    ].filter(feature => feature.label);
  }, [config, sportId]);

  const eyebrow = `Book a ${sportName ? `${sportName} court` : 'court'} at`;

  return (
    <section className="booking-intro">
      {/* Renders nothing unless the club has an active package (see
          PackageEntryButton), so an ordinary club's screen is unchanged. */}
      {packageCta && <div className="booking-package-cta">{packageCta}</div>}
      <BookingGalleryCollage
        photos={photos}
        totalPhotos={totalPhotos}
        onOpenPhoto={onOpenPhoto}
        eyebrow={eyebrow}
        clubName={clubName}
        features={features}
      />
      <BookingInfoBar
        locationText={config?.locationText}
        mapLinkUrl={getMapLinkUrl(config)}
        contactNumber={config?.contactNumber}
      />
    </section>
  );
}

export default BookingIntroV2;
